import axios from 'axios';

const rawApiUrl = process.env.REACT_APP_API_URL || 'https://timesheetapi.tickmarks.net';

const normalizedApiUrl = rawApiUrl.replace(/\/+$/, '');
const API_BASE_URL = normalizedApiUrl.endsWith('/api') ? normalizedApiUrl : `${normalizedApiUrl}/api`;
const BACKEND_BASE = API_BASE_URL.replace(/\/api$/, '');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

const authClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

const MAINTENANCE_STATUSES = new Set([502, 503, 504]);
let maintenanceActive = false;
let recoveryInterval = null;
let refreshPromise = null;

const clearAuthStorage = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
};

const redirectToLogin = () => {
  clearAuthStorage();
  window.location.href = '/';
};

const fireMaintenanceEvent = () => {
  if (!maintenanceActive) {
    maintenanceActive = true;
    window.dispatchEvent(new CustomEvent('app:maintenance'));
    if (!recoveryInterval) {
      recoveryInterval = setInterval(async () => {
        try {
          const res = await fetch(`${BACKEND_BASE}/health`, { method: 'GET', cache: 'no-store' });
          if (res.ok) {
            maintenanceActive = false;
            clearInterval(recoveryInterval);
            recoveryInterval = null;
            window.dispatchEvent(new CustomEvent('app:recovered'));
          }
        } catch (_) {}
      }, 15000);
    }
  }
};

const pingBackend = () => {
  fetch(`${BACKEND_BASE}/health`, { method: 'GET', cache: 'no-store' }).catch(() => {});
};
pingBackend();
setInterval(pingBackend, 10 * 60 * 1000);

const performTokenRefresh = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await authClient.post('/auth/refresh', { refresh_token: refreshToken });
  const tokens = response.data?.data;
  if (!tokens?.token || !tokens?.refresh_token) {
    throw new Error('Refresh response missing tokens');
  }

  localStorage.setItem('token', tokens.token);
  localStorage.setItem('refreshToken', tokens.refresh_token);
  return tokens.token;
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config || {};
    const isNetworkError = !error.response && (error.code === 'ECONNABORTED' || error.message === 'Network Error');

    if (MAINTENANCE_STATUSES.has(status) || isNetworkError) {
      fireMaintenanceEvent();
    }

    if (status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        if (!refreshPromise) {
          refreshPromise = performTokenRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newAccessToken = await refreshPromise;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (identifier, password) => {
    const response = await api.post('/auth/login', { identifier, password });
    return response;
  },
  logout: () => api.post('/auth/logout', { refresh_token: localStorage.getItem('refreshToken') }),
  refreshToken: (refreshToken) => authClient.post('/auth/refresh', { refresh_token: refreshToken }),
  register: (data) => api.post('/auth/register', data),
  verifyEmail: (token) => authClient.get('/auth/verify-email', { params: { token } }),
  resendVerification: (email) => authClient.post('/auth/resend-verification', { email }, { params: { email } }),
  requestPasswordReset: (email) => api.post('/auth/request-password-reset', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  verifyFirstLogin: (data) => api.post('/auth/verify-first-login', data),
  getCurrentUser: () => api.get('/users/me'),
};

export const usersAPI = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  updateRoles: (id, roles) => api.put(`/users/${id}/roles`, roles),
  getManagers: () => api.get('/users/managers/list'),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { new_password: newPassword }),
  updateMyPassword: (data) => api.post('/users/me/password', data),
  getMyProfile: () => api.get('/users/me'),
  updateMyUsername: (username) => api.patch(`/users/me/username?username=${encodeURIComponent(username)}`),
  updateMyTimezone: (timezone) => api.patch(`/users/me/timezone?timezone=${encodeURIComponent(timezone)}`),
};

export const projectsAPI = {
  list: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  getMembers: (id) => api.get(`/projects/${id}/members`),
  addMember: (id, data) => api.post(`/projects/${id}/members`, data),
  removeMember: (projectId, userId) => api.delete(`/projects/${projectId}/members/${userId}`),
};

export const timeEntriesAPI = {
  list: (params) => api.get('/time-entries', { params }),
  get: (id) => api.get(`/time-entries/${id}`),
  create: (data) => api.post('/time-entries', data),
  update: (id, data) => api.put(`/time-entries/${id}`, data),
  delete: (id) => api.delete(`/time-entries/${id}`),
  approve: (id, data) => api.post(`/time-entries/${id}/approve`, data),
  reject: (id, data) => api.post(`/time-entries/${id}/reject`, data),
  resubmit: (id, data) => api.post(`/time-entries/${id}/resubmit`, data),
  getPendingApprovals: (params) => api.get('/time-entries/pending/approvals', { params }),
  bulkApprove: (entryIds) => api.post('/time-entries/bulk-approve', { entry_ids: entryIds }),
  bulkReject: (entryIds, reason) => api.post('/time-entries/bulk-reject', { entry_ids: entryIds, rejection_reason: reason }),
  bulkApproveFiltered: (params) => api.post('/time-entries/bulk-approve-filtered', null, { params }),
  addComment: (id, comment) => api.post(`/time-entries/${id}/comments`, { comment }),
};

export const reportsAPI = {
  getSummary: (params) => api.get('/reports/summary', { params }),
  getDaily: (params) => api.get('/reports/daily', { params }),
  getByReportingArea: (params) => api.get('/reports/by-reporting-area', { params }),
  getUserReport: (userId, params) => api.get(`/reports/user/${userId}`, { params }),
};

export const notificationsAPI = {
  getNotifications: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  clearAll: () => api.post('/notifications/clear-all'),
};

export const importAPI = {
  users: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/users/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  projects: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/projects/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  timeEntries: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/time-entries/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const supportAPI = {
  submitFeedback: (data) => api.post('/support/feedback', data),
};

export const domainsAPI = {
  list: () => api.get('/domains'),
  getPublic: () => api.get('/domains/public'),
  add: (domain) => api.post('/domains', { domain }),
  delete: (id) => api.delete(`/domains/${id}`),
};

export const smtpAPI = {
  get: () => api.get('/settings/smtp'),
  update: (data) => api.put('/settings/smtp', data),
  test: (data) => api.post('/settings/smtp/test', data),
  getQueue: (limit = 25) => api.get(`/settings/smtp/queue?limit=${limit}`),
  retryPending: () => api.post('/settings/smtp/retry-pending'),
};

export const envAPI = {
  list: () => api.get('/settings/env'),
  update: (key, value, adminPassword) => api.put('/settings/env', { key, value, admin_password: adminPassword }),
  testDb: (databaseUrl) => api.post('/settings/env/test-db', { database_url: databaseUrl }),
  getAudit: (limit = 50) => api.get(`/settings/env/audit?limit=${limit}`),
};

export default api;
